// @ts-nocheck
import { Trans, useLingui } from "@lingui/react/macro";
import {
	Button,
	CopyButton,
	Flex,
	Group,
	Loader,
	Modal,
	Stack,
	Text,
	TextInput,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconTrash } from "@tabler/icons-react";
import { useState } from "react";
import UsageLimit from "@/components/common/UsageLimit/UsageLimit";
import { HelpIcon } from "@/components/common/ui/feedback/HelpIcon/HelpIcon";
import httpClient from "@/config/httpClient";
import { handleApiResponse } from "@/utils/common/api/handleApiResponse";
import { EMPTY_OBJECT } from "@/utils/common/defaults";
import { endpoints } from "@/utils/common/requests/endpoints";
import { crewMembersQueries } from "@/utils/useQuery/queries/queries";
import { useFetchFromQuery } from "@/utils/useQuery/useFetch";
import classes from "./CrewMembers.module.css";
import CrewMember from "./partials/CrewMember/CrewMember";
import { useDeleteCrewMember } from "./requests/useDeleteCrewMember";
import { useUpdateCrewMemberRole } from "./requests/useUpdateCrewMemberRole";

export default function CrewMembers({
	opened = true,
	onClose = () => {},
	crew = EMPTY_OBJECT,
	onRenewInvitationId = () => {},
}) {
	const { t } = useLingui();
	const {
		data: crewMembersData,
		error,
		isLoading,
		invalidateKey,
	} = useFetchFromQuery(crewMembersQueries.getAll, {
		args: {
			crewId: crew?.id,
		},
		enabled: Boolean(crew?.id),
	});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isMutatingUser, setIsMutatingUser] = useState(null);

	const { trigger: updateCrewMemberRole } = useUpdateCrewMemberRole();
	const { trigger: deleteCrewMember } = useDeleteCrewMember({
		crewId: crew?.id,
		userId: isMutatingUser,
	});

	const invitationLink = `${import.meta.env.VITE_APP_URL}/crew-invitation?id=${crew?.invitationId}`;

	const handleRenew = async () => {
		setIsSubmitting(true);
		const { response } = await httpClient.patch(endpoints.crew.RENEW_INVITATION(crew?.id), {});

		if (response.invitationId) {
			onRenewInvitationId(response.invitationId);
			onClose();
		}

		setIsSubmitting(false);
	};

	const handleConfirmDeleteMember = ({ user }) => {
		modals.openConfirmModal({
			title: t`Expulsar miembro`,
			centered: true,
			children: (
				<Text size="sm">
					<Trans>
						Esta acción expulsará a{" "}
						<Text component="span" c="blue">
							{user.username}
						</Text>{" "}
						de tu espacio{" "}
						<Text component="span" c="blue">
							{crew.name}
						</Text>
						.<br />
						Podrás volver a enviarle un enlace de invitación para que vuelva a unirse.
					</Trans>
				</Text>
			),
			labels: {
				confirm: t`Expulsar miembro`,
				cancel: t`Cancelar`,
			},
			confirmProps: {
				color: "red",
				leftSection: <IconTrash />,
			},
			onConfirm: () =>
				handleDeleteCrewMember({
					userId: user.id,
					role: user.role,
				}),
		});
	};

	async function handleChangeRole({ userId, user, role }) {
		try {
			if (role === "#delete") {
				handleConfirmDeleteMember({
					user,
				});

				return;
			}

			setIsMutatingUser(userId);

			const apiResponse = await updateCrewMemberRole({
				crewId: crew?.id,
				payload: {
					userId,
					role,
				},
			});

			handleApiResponse({
				id: "update_crew_member_role",
				apiResponse: apiResponse,
				successTitle: t`Rol cambiado con éxito`,
				onSuccess: () => {
					invalidateKey(crewMembersQueries.getAll.key);
				},
			});
		} finally {
			setIsMutatingUser(null);
		}
	}

	async function handleDeleteCrewMember({ userId }) {
		try {
			setIsMutatingUser(userId);

			const apiResponse = await deleteCrewMember({
				userId,
			});

			handleApiResponse({
				id: "delete_crew_member",
				apiResponse: apiResponse,
				successTitle: t`Miembro expulsado con éxito`,
				onSuccess: () => {
					invalidateKey(crewMembersQueries.getAll.key);
				},
			});
		} finally {
			setIsMutatingUser(null);
		}
	}

	if (isLoading) return <Loader />;
	if (error)
		return (
			<h1>
				<Trans>Error</Trans>
			</h1>
		);

	return (
		<Modal
			opened={opened}
			onClose={onClose}
			title={
				<Text span size="xl" fw="bold">
					<Trans>Gestionar miembros del espacio</Trans>
				</Text>
			}
		>
			<Stack gap="xl">
				<Stack>
					<Text size="sm" fw={500}>
						<Trans>Enlace de invitación</Trans>
					</Text>

					<Flex gap="md" align="center">
						<TextInput
							value={invitationLink}
							className={classes.flexOne}
							readOnly
							onFocus={(ev) => {
								// Select all text
								ev.target.select();
							}}
						/>
						<CopyButton value={invitationLink}>
							{({ copied, copy }) => (
								<Button
									color={copied ? "teal" : "blue"}
									onClick={copy}
									loading={isSubmitting}
								>
									{copied ? t`¡Copiado!` : t`Copiar`}
								</Button>
							)}
						</CopyButton>
					</Flex>

					<Flex gap="md" align="center">
						<Button
							variant="light"
							style={{ flex: 1 }}
							onClick={handleRenew}
							loading={isSubmitting}
						>
							<Trans>Renovar enlace de invitación</Trans>
						</Button>

						<HelpIcon
							width={300}
							label={t`Esta acción invalidará el enlace de invitación actual y creará uno nuevo. Todas las invitaciones enviadas y no aceptadas, dejarán de funcionar.`}
						/>
					</Flex>
				</Stack>

				<Stack>
					<Group justify="space-between">
						<Text size="sm" fw={500}>
							<Trans>Miembros</Trans>
						</Text>

						<UsageLimit feature="crew.totalMembers" />
					</Group>

					<Stack>
						{(crewMembersData?.members ?? []).map((_member) => {
							return (
								<CrewMember
									key={_member.id}
									crewMember={_member}
									onChangeRole={({ userId, user, role }) => {
										handleChangeRole({ userId, user, role });
									}}
									isLoading={isMutatingUser === _member.id}
								/>
							);
						})}
					</Stack>
				</Stack>
			</Stack>
		</Modal>
	);
}
